import React, { useState, useRef } from 'react';
import { Upload, X, FileText, AlertCircle, CheckCircle, Eye, EyeOff, Info } from 'lucide-react';
import Button from './Button';
import './DataImportModal.css';

/**
 * 데이터 가져오기 모달
 * JSON 파일 업로드 및 마스터 패스워드 복호화 지원
 */
function DataImportModal({ isOpen, onClose, onImport, loading = false }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [masterPassword, setMasterPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [importOptions, setImportOptions] = useState({
    mergeMode: 'merge', // merge, replace
    skipDuplicates: true,
    preserveTimestamps: true
  });
  const [previewData, setPreviewData] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showRestorationGuide, setShowRestorationGuide] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      alert('JSON 파일만 업로드할 수 있습니다.');
      return;
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB 제한
      alert('파일 크기는 50MB를 초과할 수 없습니다.');
      return;
    }

    setSelectedFile(file);
    readFileContent(file);
  };

  const readFileContent = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = JSON.parse(e.target.result);
        setFileContent(content);
        
        // 암호화된 파일인지 확인
        if (content.encrypted === true || content.type === 'password-wallet-export') {
          setIsEncrypted(true);
          setPreviewData(null);
        } else {
          setIsEncrypted(false);
          validateAndPreviewData(content);
        }
        
        setValidationErrors([]);
      } catch (error) {
        setValidationErrors(['유효하지 않은 JSON 파일입니다.']);
        setFileContent(null);
        setPreviewData(null);
      }
    };
    reader.onerror = () => {
      setValidationErrors(['파일을 읽을 수 없습니다.']);
    };
    reader.readAsText(file);
  };

  const validateAndPreviewData = (data) => {
    const errors = [];
    let preview = {
      passwords: 0,
      creditCards: 0,
      apiKeys: 0,
      secureNotes: 0,
      attachments: 0,
      totalItems: 0
    };

    try {
      // 데이터 구조 검증
      if (!data.metadata || !data.data) {
        errors.push('올바른 Password Wallet 내보내기 파일이 아닙니다.');
        setValidationErrors(errors);
        return;
      }

      // 각 데이터 타입 개수 계산
      if (data.data.passwords && Array.isArray(data.data.passwords)) {
        preview.passwords = data.data.passwords.length;
        preview.totalItems += preview.passwords;
      }

      if (data.data.creditCards && Array.isArray(data.data.creditCards)) {
        preview.creditCards = data.data.creditCards.length;
        preview.totalItems += preview.creditCards;
      }

      if (data.data.apiKeys && Array.isArray(data.data.apiKeys)) {
        preview.apiKeys = data.data.apiKeys.length;
        preview.totalItems += preview.apiKeys;
      }

      if (data.data.secureNotes && Array.isArray(data.data.secureNotes)) {
        preview.secureNotes = data.data.secureNotes.length;
        preview.totalItems += preview.secureNotes;
        
        // 첨부파일 개수 계산
        data.data.secureNotes.forEach(note => {
          if (note.attachments && Array.isArray(note.attachments)) {
            preview.attachments += note.attachments.length;
          }
        });
      }

      if (preview.totalItems === 0) {
        errors.push('가져올 수 있는 데이터가 없습니다.');
      }

      setPreviewData(preview);
      setValidationErrors(errors);
    } catch (error) {
      errors.push('데이터 구조를 분석할 수 없습니다.');
      setValidationErrors(errors);
    }
  };

  const handleDecrypt = async () => {
    if (!masterPassword || masterPassword.length < 8) {
      setValidationErrors(['마스터 패스워드는 최소 8자 이상이어야 합니다.']);
      return;
    }

    try {
      // importService에서 복호화 처리
      const { importService } = await import('../../services/importService');
      const decryptedData = await importService.decryptImportData(fileContent, masterPassword);
      
      setFileContent(decryptedData);
      setIsEncrypted(false);
      validateAndPreviewData(decryptedData);
      setMasterPassword('');
      setValidationErrors([]);
    } catch (error) {
      console.error('Decryption error:', error);
      setValidationErrors(['패스워드가 올바르지 않거나 파일이 손상되었습니다.']);
    }
  };

  const handleImport = () => {
    if (!fileContent || !previewData || validationErrors.length > 0) {
      return;
    }

    const importConfig = {
      data: fileContent,
      options: importOptions,
      preview: previewData,
      filename: selectedFile?.name
    };

    onImport(importConfig);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setFileContent(null);
    setIsEncrypted(false);
    setMasterPassword('');
    setPreviewData(null);
    setValidationErrors([]);
    setImportOptions({
      mergeMode: 'merge',
      skipDuplicates: true,
      preserveTimestamps: true
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (!loading) {
      handleReset();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="import-modal-overlay">
      <div className="import-modal">
        <div className="import-modal-header">
          <div className="header-content">
            <Upload size={24} className="import-icon" />
            <h2>데이터 가져오기</h2>
          </div>
          <div className="header-actions">
            <button
              className="guide-button"
              onClick={() => setShowRestorationGuide(true)}
              title="복원 가이드 보기"
            >
              <Info size={16} />
              가이드
            </button>
            <button 
              className="close-button" 
              onClick={handleClose}
              disabled={loading}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="import-modal-body">
          {/* 파일 업로드 섹션 */}
          <div className="upload-section">
            <h3>파일 선택</h3>
            <div className="file-upload-area">
              {!selectedFile ? (
                <div className="upload-placeholder">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json,application/json"
                    onChange={handleFileSelect}
                    className="file-input"
                  />
                  <FileText size={48} className="upload-icon" />
                  <p className="upload-text">
                    JSON 파일을 선택하거나 드래그하세요
                  </p>
                  <p className="upload-hint">
                    Password Wallet에서 내보낸 JSON 파일만 지원됩니다
                  </p>
                </div>
              ) : (
                <div className="file-selected">
                  <div className="file-info">
                    <FileText size={24} className="file-icon" />
                    <div className="file-details">
                      <span className="file-name">{selectedFile.name}</span>
                      <span className="file-size">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  </div>
                  <button
                    className="file-remove"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 암호화된 파일 복호화 */}
          {isEncrypted && fileContent && (
            <div className="decrypt-section">
              <h3>파일 복호화</h3>
              <p className="decrypt-description">
                이 파일은 마스터 패스워드로 암호화되어 있습니다. 
                내보낼 때 설정한 마스터 패스워드를 입력하세요.
              </p>
              <div className="password-input-group">
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                    placeholder="마스터 패스워드 입력"
                    className="master-password-input"
                    disabled={loading}
                  />
                  <button
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    type="button"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <Button
                  variant="primary"
                  onClick={handleDecrypt}
                  disabled={!masterPassword || masterPassword.length < 8 || loading}
                >
                  복호화
                </Button>
              </div>
            </div>
          )}

          {/* 데이터 미리보기 */}
          {previewData && !isEncrypted && (
            <div className="preview-section">
              <h3>가져올 데이터 미리보기</h3>
              <div className="preview-grid">
                <div className="preview-item">
                  <span className="preview-label">패스워드</span>
                  <span className="preview-count">{previewData.passwords}개</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">신용카드</span>
                  <span className="preview-count">{previewData.creditCards}개</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">API 키</span>
                  <span className="preview-count">{previewData.apiKeys}개</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">보안 메모</span>
                  <span className="preview-count">{previewData.secureNotes}개</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">첨부파일</span>
                  <span className="preview-count">{previewData.attachments}개</span>
                </div>
                <div className="preview-item total">
                  <span className="preview-label">전체</span>
                  <span className="preview-count">{previewData.totalItems}개</span>
                </div>
              </div>
            </div>
          )}

          {/* 가져오기 옵션 */}
          {previewData && !isEncrypted && (
            <div className="options-section">
              <h3>가져오기 옵션</h3>
              
              <div className="option-group">
                <label className="option-label">중복 처리 방식</label>
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="mergeMode"
                      value="merge"
                      checked={importOptions.mergeMode === 'merge'}
                      onChange={(e) => setImportOptions(prev => ({
                        ...prev,
                        mergeMode: e.target.value
                      }))}
                    />
                    <span>병합</span>
                    <small>기존 데이터와 합치기</small>
                  </label>
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="mergeMode"
                      value="replace"
                      checked={importOptions.mergeMode === 'replace'}
                      onChange={(e) => setImportOptions(prev => ({
                        ...prev,
                        mergeMode: e.target.value
                      }))}
                    />
                    <span>대체</span>
                    <small>기존 데이터를 모두 삭제하고 가져오기</small>
                  </label>
                </div>
              </div>

              <div className="checkbox-group">
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={importOptions.skipDuplicates}
                    onChange={(e) => setImportOptions(prev => ({
                      ...prev,
                      skipDuplicates: e.target.checked
                    }))}
                  />
                  <span>중복 데이터 건너뛰기</span>
                  <small>동일한 데이터가 있으면 가져오지 않습니다</small>
                </label>

                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={importOptions.preserveTimestamps}
                    onChange={(e) => setImportOptions(prev => ({
                      ...prev,
                      preserveTimestamps: e.target.checked
                    }))}
                  />
                  <span>생성/수정 시간 보존</span>
                  <small>원본 파일의 시간 정보를 유지합니다</small>
                </label>
              </div>
            </div>
          )}

          {/* 에러 메시지 */}
          {validationErrors.length > 0 && (
            <div className="error-section">
              <AlertCircle size={16} />
              <div className="error-list">
                {validationErrors.map((error, index) => (
                  <p key={index} className="error-message">{error}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="import-modal-footer">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={!previewData || isEncrypted || validationErrors.length > 0 || loading}
            loading={loading}
          >
            <Upload size={16} />
            {loading ? '가져오는 중...' : '가져오기'}
          </Button>
        </div>
      </div>

      {/* 복원 가이드 모달 */}
      {showRestorationGuide && (
        <RestorationGuideModal 
          onClose={() => setShowRestorationGuide(false)} 
        />
      )}
    </div>
  );
}

/**
 * 복원 가이드 모달
 */
function RestorationGuideModal({ onClose }) {
  return (
    <div className="guide-modal-overlay">
      <div className="guide-modal">
        <div className="guide-modal-header">
          <h2>데이터 복원 가이드</h2>
          <button className="close-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="guide-modal-body">
          <div className="guide-section">
            <h3>1. 내보낸 데이터 준비</h3>
            <p>Password Wallet에서 내보낸 JSON 파일을 준비하세요.</p>
            <ul>
              <li>암호화된 파일: 마스터 패스워드가 필요합니다</li>
              <li>암호화되지 않은 파일: 바로 가져올 수 있습니다</li>
            </ul>
          </div>
          
          <div className="guide-section">
            <h3>2. 파일 업로드</h3>
            <p>JSON 파일을 선택하면 자동으로 내용을 분석합니다.</p>
            <ul>
              <li>최대 50MB까지 업로드 가능</li>
              <li>JSON 형식만 지원</li>
            </ul>
          </div>
          
          <div className="guide-section">
            <h3>3. 암호화된 파일 복호화</h3>
            <p>마스터 패스워드로 암호화된 파일의 경우:</p>
            <ul>
              <li>내보낼 때 설정한 마스터 패스워드 입력</li>
              <li>복호화 후 데이터 미리보기 가능</li>
            </ul>
          </div>
          
          <div className="guide-section">
            <h3>4. 가져오기 옵션 설정</h3>
            <ul>
              <li><strong>병합:</strong> 기존 데이터와 합치기</li>
              <li><strong>대체:</strong> 기존 데이터 삭제 후 가져오기</li>
              <li><strong>중복 건너뛰기:</strong> 동일한 데이터 제외</li>
              <li><strong>시간 보존:</strong> 원본 생성/수정 시간 유지</li>
            </ul>
          </div>
          
          <div className="guide-section">
            <h3>5. 주의사항</h3>
            <ul>
              <li>대체 모드는 기존 데이터를 완전히 삭제합니다</li>
              <li>첨부파일은 Base64로 인코딩되어 복원됩니다</li>
              <li>가져오기 전에 현재 데이터를 백업하는 것을 권장합니다</li>
            </ul>
          </div>
        </div>
        <div className="guide-modal-footer">
          <Button variant="primary" onClick={onClose}>
            확인
          </Button>
        </div>
      </div>
    </div>
  );
}

export default DataImportModal;